import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ChartDataset } from 'chart.js';
import { ArrowLeft, Download, RotateCcw, Search, SlidersHorizontal, X } from 'lucide-react';
import { ChartBox } from '../components/ChartBox';
import { KpiCard } from '../components/KpiCard';
import { RankList } from '../components/RankList';
import { RateCell, pctText } from '../components/RateCell';
import { Tag } from '../components/Tag';
import { loadOnlineConversationRaw } from '../api';
import type { OnlineRawConversationResponse, OnlineRawMessage, SecondhandRecord, SecondhandResponse, SecondhandSalesResponse } from '../types';
import { SecondhandSalesSection } from './SecondhandSalesSection';
import { SecondhandLarkSection } from './SecondhandLarkSection';
import { SecondhandPinBar } from './SecondhandPinBar';
import { num } from '../utils';
import { DEFAULT_RANGE } from '../ranges';
import { TARGET_COLOR, TARGET_PARENTS, parentOf } from '../targetGroups';
import subcatConfig from '../../../local-ai/config/masterdata_categories.json';

interface SecondhandScreenProps {
  data: SecondhandResponse | null;
  loaded: boolean;
  busy: boolean;
  range: string;
  onRange: (range: string) => void;
  sales?: SecondhandSalesResponse | null;
  salesBusy?: boolean;
  salesError?: string;
}

type Gran = 'day' | 'week' | 'month';
type Dim = 'periods' | 'agents' | 'targets' | 'subcats' | 'intents' | 'offers' | 'outcomes' | 'stock' | 'missed';
type Filters = Record<Dim, string[]>;
const EMPTY_FILTERS: Filters = {
  periods: [], agents: [], targets: [], subcats: [], intents: [], offers: [], outcomes: [], stock: [], missed: [],
};
const ALL_DIMS = Object.keys(EMPTY_FILTERS) as Dim[];
const QUICK_DIMS: Dim[] = ['agents', 'targets'];
const DETAIL_DIMS: Dim[] = ['periods', 'subcats', 'intents', 'offers', 'outcomes', 'stock', 'missed'];

// PIM leaf → readable name, same masterdata file the classifier labels from.
const SUB_LABEL: Record<string, string> = Object.fromEntries(
  (subcatConfig.groups as unknown as Array<{ pim_subcategories?: Array<{ code?: string; name?: string }> }>)
    .flatMap(g => (g.pim_subcategories || []).filter(s => s.code).map(s => [String(s.code), s.name || String(s.code)] as [string, string])),
);

const INTENT_ORDER = ['buy_used', 'trade_in', 'both'];
const INTENT_LABEL: Record<string, string> = { buy_used: 'Mua đồ cũ', trade_in: 'Thu cũ đổi mới', both: 'Cả hai', none: 'Không rõ' };
// Quick-bar labels: short so store/target/intent fit on one row.
const INTENT_SHORT: Record<string, string> = { trade_in: 'Thu cũ' };
const INTENT_TONE: Record<string, 'blue' | 'violet' | 'teal' | 'gray'> = { buy_used: 'blue', trade_in: 'violet', both: 'teal', none: 'gray' };
const OFFER_LABEL: Record<string, string> = {
  proactive: 'Chủ động', on_request: 'Khi khách hỏi', link_only: 'Chỉ gửi link',
  declined_no_stock: 'Từ chối · hết hàng', none: 'Không giới thiệu',
};
const OFFER_TONE: Record<string, 'green' | 'blue' | 'amber' | 'red' | 'gray'> = {
  proactive: 'green', on_request: 'blue', link_only: 'amber', declined_no_stock: 'red', none: 'gray',
};
const STOCK_LABEL: Record<string, string> = { available: 'Còn hàng', unavailable: 'Không có hàng', unknown: 'Chưa rõ' };
const STOCK_TONE: Record<string, 'green' | 'red' | 'gray'> = { available: 'green', unavailable: 'red', unknown: 'gray' };
const OUTCOME_LABEL: Record<string, string> = { closed: 'Chốt', hopeful: 'Có hy vọng', lost: 'Mất', unknown: 'Chưa rõ' };
const OUTCOME_TONE: Record<string, 'green' | 'amber' | 'red' | 'gray'> = { closed: 'green', hopeful: 'amber', lost: 'red', unknown: 'gray' };
const DIM_TITLE: Record<Dim, string> = {
  periods: 'Kỳ', agents: 'Sale', targets: 'Ngành target', subcats: 'Nhóm hàng chi tiết',
  intents: 'Ý định', offers: 'Giới thiệu', outcomes: 'Kết quả', stock: 'Tồn kho', missed: 'Cơ hội bỏ lỡ',
};

// ---- time bucketing (client-side day/week/month) ----
function isoWeek(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const dayNum = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dayNum + 3);
  const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((d.getTime() - firstThu.getTime()) / 86400000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}
function bucketOf(r: SecondhandRecord, g: Gran): string {
  const day = r.day || '';
  if (!day) return '';
  if (g === 'month') return day.slice(0, 7);
  return g === 'day' ? day : isoWeek(day);
}
function periodLabel(code: string): string {
  if (/^\d{4}-W\d{2}$/.test(code)) return `T${code.slice(6)}/${code.slice(2, 4)}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(code)) return `${code.slice(8)}/${code.slice(5, 7)}`;
  if (/^\d{4}-\d{2}$/.test(code)) return `Th${code.slice(5)}/${code.slice(2, 4)}`;
  return code;
}

function valueOf(r: SecondhandRecord, dim: Dim, gran: Gran): string {
  if (dim === 'periods') return bucketOf(r, gran);
  if (dim === 'agents') return r.agent_name || 'Chưa rõ';
  if (dim === 'targets') return parentOf(r);
  if (dim === 'subcats') return r.ai_subcategory || '';
  if (dim === 'intents') return r.secondhand_intent || 'none';
  if (dim === 'offers') return r.staff_offer_type || 'none';
  if (dim === 'outcomes') return r.outcome || 'unknown';
  if (dim === 'stock') return r.stock_status || 'unknown';
  return r.missed_opportunity ? '1' : '';
}

function labelFor(dim: Dim, code: string): string {
  if (dim === 'targets') return TARGET_PARENTS.find(p => p.code === code)?.label || code;
  if (dim === 'subcats') return SUB_LABEL[code] || code;
  if (dim === 'intents') return INTENT_LABEL[code] || code;
  if (dim === 'offers') return OFFER_LABEL[code] || code;
  if (dim === 'outcomes') return OUTCOME_LABEL[code] || code;
  if (dim === 'stock') return STOCK_LABEL[code] || code;
  if (dim === 'periods') return periodLabel(code);
  if (dim === 'missed') return 'Cơ hội bỏ lỡ';
  return code;
}

function countByKey(records: SecondhandRecord[], keyFn: (r: SecondhandRecord) => string): Array<[string, number]> {
  const m = new Map<string, number>();
  records.forEach(r => { const k = keyFn(r); if (k) m.set(k, (m.get(k) || 0) + 1); });
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}
function countNoStock(records: SecondhandRecord[], keyFn: (r: SecondhandRecord) => string): Map<string, number> {
  const m = new Map<string, number>();
  records.forEach(r => {
    if (r.stock_status !== 'unavailable') return;
    const k = keyFn(r); if (!k) return;
    m.set(k, (m.get(k) || 0) + 1);
  });
  return m;
}

function inRange(day: string | null | undefined, range: string): boolean {
  if (range === 'all') return true;
  if (!day) return false;
  const d = new Date(`${day}T00:00:00`).getTime();
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const today = now.getTime(); const D = 86400000;
  if (range === 'today') return d === today;
  if (range === 'yesterday') return d === today - D;
  const span: Record<string, number> = { '7d': 7, '28d': 28, '3m': 90, '6m': 180 };
  return d >= today - ((span[range] || 1) - 1) * D;
}

function evidenceOf(r: SecondhandRecord): string[] {
  return Array.isArray(r.evidence) ? r.evidence.map(v => String(v || '').trim()).filter(Boolean) : [];
}

function csvCell(v: unknown): string {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

interface RawState { open: boolean; loading?: boolean; data?: OnlineRawConversationResponse; error?: string; }

// "Giới thiệu" = the sale put a used option in front of the customer. Saying there is no used stock
// (declined_no_stock) is a mention, not an introduction, so it must not lift the rate.
const OFFERED = new Set(['proactive', 'on_request', 'link_only']);
const offered = (r: SecondhandRecord) => OFFERED.has(r.staff_offer_type || 'none');

export function SecondhandScreen({ data, loaded, busy, range, onRange, sales, salesBusy, salesError }: SecondhandScreenProps) {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [history, setHistory] = useState<Filters[]>([]);
  const [gran, setGran] = useState<Gran>('day');
  const [query, setQuery] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);
  const [detailLimit, setDetailLimit] = useState(100);
  const [rawConversations, setRawConversations] = useState<Record<string, RawState>>({});
  const records = data?.records || [];
  const appliedRange = data?.meta?.range || range;
  const rangedRecords = useMemo(() => records.filter(r => inRange(r.day, appliedRange)), [records, appliedRange]);

  const commit = (next: Filters) => { setHistory(h => [...h.slice(-19), filters]); setFilters(next); setDetailLimit(100); };
  const toggle = (dim: Dim, value: string) => {
    if (!value) return;
    const cur = filters[dim];
    commit({ ...filters, [dim]: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value] });
  };
  const setOne = (dim: Dim, value: string) => commit({ ...filters, [dim]: value ? [value] : [] });
  const goBack = () => {
    if (!history.length) return;
    setFilters(history[history.length - 1]);
    setHistory(history.slice(0, -1));
  };
  const clearAll = () => { commit(EMPTY_FILTERS); setQuery(''); };
  const setGranularity = (g: Gran) => { setGran(g); setFilters(prev => ({ ...prev, periods: [] })); };
  const changeRange = (v: string) => { onRange(v); setFilters(prev => ({ ...prev, periods: [] })); };

  // Page scope shared by every section (pinned bar): period + target. The online chat block keeps its own
  // finer filters; the target here drives its target filter so the three sections read the same slice.
  const [target, setTarget] = useState('');
  const [scopeHist, setScopeHist] = useState<Array<{ range: string; target: string }>>([]);
  const setScope = (next: { range?: string; target?: string }) => {
    const r = next.range ?? range;
    const t = next.target ?? target;
    if (r === range && t === target) return;
    setScopeHist(h => [...h.slice(-19), { range, target }]);
    if (r !== range) changeRange(r);
    if (t !== target) setTarget(t);
  };
  const scopeBack = () => {
    const last = scopeHist[scopeHist.length - 1];
    if (!last) return;
    setScopeHist(scopeHist.slice(0, -1));
    if (last.range !== range) changeRange(last.range);
    setTarget(last.target);
  };
  useEffect(() => { setFilters(prev => ({ ...prev, targets: target ? [target] : [] })); }, [target]);
  const activeCount = ALL_DIMS.reduce((n, d) => n + filters[d].length, 0) + (query.trim() ? 1 : 0);

  const matches = (r: SecondhandRecord, dim: Dim, vals: string[]): boolean => {
    if (vals.length === 0) return true;
    return vals.includes(valueOf(r, dim, gran));
  };

  const needle = query.trim().toLowerCase();
  const ctx = useMemo(() => {
    const base = needle
      ? rangedRecords.filter(r => `${r.summary || ''} ${r.product_text || ''}`.toLowerCase().includes(needle))
      : rangedRecords;
    const apply = (...except: Dim[]) => base.filter(r => ALL_DIMS.every(d => except.includes(d) || matches(r, d, filters[d])));
    return {
      all: apply(), periods: apply('periods'), agents: apply('agents'), targets: apply('targets'),
      subcats: apply('subcats'), intents: apply('intents'), offers: apply('offers'),
      outcomes: apply('outcomes'), stock: apply('stock'),
    };
  }, [rangedRecords, filters, gran, needle]);

  const filtered = ctx.all;
  const total = filtered.length;
  const asked = filtered.filter(r => r.customer_asked_secondhand);
  const introduced = asked.filter(offered);
  const introRate = asked.length ? introduced.length / asked.length : null;
  const proactive = filtered.filter(r => r.staff_offer_type === 'proactive').length;
  const missed = filtered.filter(r => r.missed_opportunity).length;
  const unavailable = filtered.filter(r => r.stock_status === 'unavailable').length;
  const buyUsed = asked.filter(r => r.secondhand_intent === 'buy_used').length;
  const tradeIn = asked.filter(r => r.secondhand_intent === 'trade_in').length;

  const periodEntries = useMemo(() => {
    const m = new Map<string, { n: number; asked: number; intro: number }>();
    ctx.periods.forEach(r => {
      const k = bucketOf(r, gran); if (!k) return;
      const e = m.get(k) || { n: 0, asked: 0, intro: 0 };
      e.n++;
      if (r.customer_asked_secondhand) { e.asked++; if (offered(r)) e.intro++; }
      m.set(k, e);
    });
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1));
  }, [ctx.periods, gran]);

  const agentRows = useMemo(() => {
    const m = new Map<string, { total: number; asked: number; intro: number; proactive: number; missed: number }>();
    ctx.agents.forEach(r => {
      const k = valueOf(r, 'agents', gran);
      const e = m.get(k) || { total: 0, asked: 0, intro: 0, proactive: 0, missed: 0 };
      e.total++;
      if (r.customer_asked_secondhand) { e.asked++; if (offered(r)) e.intro++; }
      if (r.staff_offer_type === 'proactive') e.proactive++;
      if (r.missed_opportunity) e.missed++;
      m.set(k, e);
    });
    return [...m.entries()].map(([agent, v]) => ({ agent, ...v }))
      .sort((a, b) => b.asked - a.asked || b.total - a.total);
  }, [ctx.agents, gran]);

  const agentOptions = useMemo(() => countByKey(rangedRecords, r => valueOf(r, 'agents', gran)), [rangedRecords, gran]);

  const demand = useMemo(() => ctx.targets.filter(r => r.customer_asked_secondhand), [ctx.targets]);
  const targetEntries = useMemo(() => countByKey(demand, r => parentOf(r))
    .sort((a, b) => TARGET_PARENTS.findIndex(p => p.code === a[0]) - TARGET_PARENTS.findIndex(p => p.code === b[0])), [demand]);
  const subEntries = useMemo(() => countByKey(demand, r => r.ai_subcategory || ''), [demand]);
  const targetNoStock = useMemo(() => countNoStock(demand, r => parentOf(r)), [demand]);
  const subNoStock = useMemo(() => countNoStock(demand, r => r.ai_subcategory || ''), [demand]);

  const targetCrumb = filters.targets.length === 1 ? filters.targets[0] : '';
  const chips = DETAIL_DIMS.flatMap(dim => filters[dim].map(val => ({ dim, val })));
  const detailCount = DETAIL_DIMS.reduce((n, d) => n + filters[d].length, 0);

  const loadRawConversation = async (conversationId: string) => {
    setRawConversations(prev => ({ ...prev, [conversationId]: { open: true, loading: true } }));
    try {
      const raw = await loadOnlineConversationRaw(conversationId);
      setRawConversations(prev => ({
        ...prev,
        [conversationId]: { open: prev[conversationId]?.open ?? true, loading: false, data: raw },
      }));
    } catch (error) {
      setRawConversations(prev => ({
        ...prev,
        [conversationId]: {
          open: prev[conversationId]?.open ?? true,
          loading: false,
          error: String((error as Error)?.message || error),
        },
      }));
    }
  };

  const toggleRawConversation = async (conversationId: string) => {
    const current = rawConversations[conversationId];
    if (current?.open) {
      setRawConversations(prev => ({ ...prev, [conversationId]: { ...prev[conversationId], open: false } }));
      return;
    }
    if (current?.data) {
      setRawConversations(prev => ({ ...prev, [conversationId]: { ...prev[conversationId], open: true } }));
      return;
    }
    if (current?.loading) return;
    await loadRawConversation(conversationId);
  };


  const exportCsv = () => {
    const head = ['ngay', 'sale', 'nhom_target', 'nhom_chi_tiet', 'y_dinh', 'gioi_thieu', 'ton_kho', 'ket_qua', 'co_hoi_bo_lo', 'tom_tat', 'trich_dan'];
    const lines = filtered.map(r => [
      r.day, r.agent_name || '', labelFor('targets', parentOf(r)),
      r.ai_subcategory ? labelFor('subcats', r.ai_subcategory) : '',
      labelFor('intents', r.secondhand_intent || 'none'), labelFor('offers', r.staff_offer_type || 'none'),
      labelFor('stock', r.stock_status || 'unknown'), labelFor('outcomes', r.outcome || 'unknown'),
      r.missed_opportunity ? 'Có' : 'Không', r.summary || '', evidenceOf(r).join(' | '),
    ].map(csvCell).join(','));
    const blob = new Blob([`﻿${[head.join(','), ...lines].join('\n')}`], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `hang-cu-online-${appliedRange}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Doanh số hàng cũ (f_sales) luôn đứng đầu trang, kể cả khi phần hội thoại chưa có dữ liệu.
  const pinBar = (
    <SecondhandPinBar range={range} onRange={v => setScope({ range: v })} busy={busy || !!salesBusy}
      target={target} onTarget={t => setScope({ target: t })} canBack={scopeHist.length > 0} onBack={scopeBack}
      canClear={range !== DEFAULT_RANGE || !!target} onClear={() => setScope({ range: DEFAULT_RANGE, target: '' })} />
  );
  const salesBlock = <SecondhandSalesSection data={sales || null} busy={!!salesBusy} error={salesError || ''} range={range} target={target} />;
  // Sale hỏi team Dự án trên LARK: nối doanh số (trên) với nhu cầu khách online (dưới).
  const larkBlock = <SecondhandLarkSection range={range} target={target} />;
  const chatTitle = (
    <div id="sh-online" className="section-title space ins-anchor"><h2>Hội thoại online hỏi hàng cũ</h2><span>Local-AI phân loại từng hội thoại Vpage</span></div>
  );
  if (busy && !loaded) return <>{pinBar}{salesBlock}{larkBlock}{chatTitle}<div className="card empty">Đang tải dữ liệu hàng cũ online...</div></>;
  if (loaded && records.length === 0) return <>{pinBar}{salesBlock}{larkBlock}{chatTitle}<div className="card empty">Chưa có dữ liệu — luồng phân loại hàng cũ chưa chạy cho khoảng này.</div></>;

  const selectOf = (dim: Dim, src: SecondhandRecord[], all: string) => {
    const cur = filters[dim];
    const opts = countByKey(src, r => valueOf(r, dim, gran));
    return (
      <select className={`ins-select ${cur.length ? 'has-value' : ''}`} aria-label={DIM_TITLE[dim]} value={cur.length === 1 ? cur[0] : cur.length ? '__multi' : ''}
        onChange={e => e.target.value !== '__multi' && setOne(dim, e.target.value)}>
        <option value="">{all}</option>
        {cur.length > 1 && <option value="__multi">{DIM_TITLE[dim]}: {cur.length} đã chọn</option>}
        {opts.map(([k, n]) => <option key={k} value={k}>{labelFor(dim, k)} · {num(n)}</option>)}
      </select>
    );
  };

  return (
    <>
      {pinBar}
      {salesBlock}
      {larkBlock}
      {chatTitle}
      <div style={{ margin: '0 0 12px' }}>
        <nav className={`ins-scopebar ${activeCount ? 'is-filtered' : ''}`} aria-label="Bộ lọc">
          <div className="ins-row">
            <div className="ins-crumbs">
              <b>{filters.agents.length ? filters.agents.join(', ') : 'Mọi sale'}</b>
              {targetCrumb && <span>› {labelFor('targets', targetCrumb)}</span>}
              <em>{num(total)} / {num(rangedRecords.length)} hội thoại</em>
              {chips.map(({ dim, val }) => (
                <button key={`${dim}:${val}`} type="button" className="ins-chip" onClick={() => toggle(dim, val)} title="Bỏ lọc này">
                  <span>{DIM_TITLE[dim]}:</span> {labelFor(dim, val)} <X size={12} />
                </button>
              ))}
            </div>
            <div className="ins-actions">
              <label className={`ins-search ${query ? 'has-value' : ''}`} title="Tìm trong tóm tắt AI và sản phẩm khách hỏi">
                <Search size={14} />
                <input type="search" value={query} placeholder="Tìm sản phẩm, tóm tắt…" onChange={e => { setQuery(e.target.value); setDetailLimit(100); }} />
              </label>
              <button type="button" className={`ins-detail-toggle ${moreOpen ? 'is-on' : ''} ${detailCount ? 'has-value' : ''}`} aria-expanded={moreOpen} onClick={() => setMoreOpen(v => !v)} title="Lọc chi tiết: nhóm hàng, ý định, cách giới thiệu, kết quả, tồn kho">
                <SlidersHorizontal size={15} /><span>Lọc chi tiết</span>{detailCount > 0 && <b>{detailCount}</b>}
              </button>
              <button type="button" className="ins-iconbtn" onClick={goBack} disabled={!history.length} title="Quay lại bộ lọc trước" aria-label="Quay lại"><ArrowLeft size={15} /></button>
              <button type="button" className="ins-clear" onClick={clearAll} disabled={!activeCount} title="Xóa tất cả lọc"><RotateCcw size={15} /><span>Xóa lọc</span></button>
              <button type="button" className="ins-iconbtn" onClick={exportCsv} disabled={!total} title="Xuất CSV theo bộ lọc (đã ẩn PII)" aria-label="Xuất CSV"><Download size={15} /></button>
            </div>
          </div>
          <div className="ins-quickbar" role="group" aria-label="Lọc nhanh">
            <div className="ins-seg" role="group" aria-label="Ngành target">
              <button type="button" aria-pressed={!filters.targets.length} onClick={() => setOne('targets', '')}>Mọi ngành</button>
              {TARGET_PARENTS.map(p => (
                <button key={p.code} type="button" aria-pressed={filters.targets.includes(p.code)} onClick={() => toggle('targets', p.code)} title={p.label}>
                  {p.code === 'OTHER' ? 'Khác' : p.label}
                </button>
              ))}
            </div>
            <div className="ins-seg" role="group" aria-label="Ý định hàng cũ">
              <button type="button" aria-pressed={!filters.intents.length} onClick={() => setOne('intents', '')}>Mọi ý định</button>
              {INTENT_ORDER.map(v => (
                <button key={v} type="button" aria-pressed={filters.intents.includes(v)} onClick={() => toggle('intents', v)} title={INTENT_LABEL[v]}>{INTENT_SHORT[v] || INTENT_LABEL[v]}</button>
              ))}
            </div>
          </div>
          {moreOpen && (
            <div className="ins-drawer">
              <select className={`ins-select ${filters.agents.length ? 'has-value' : ''}`} aria-label="Sale" value={filters.agents.length === 1 ? filters.agents[0] : filters.agents.length ? '__multi' : ''}
              onChange={e => e.target.value !== '__multi' && setOne('agents', e.target.value)}>
              <option value="">Mọi sale</option>
              {filters.agents.length > 1 && <option value="__multi">Sale: {filters.agents.length} đã chọn</option>}
              {agentOptions.map(([k, n]) => <option key={k} value={k}>{k} · {num(n)}</option>)}
            </select>
              <div className="ins-seg" role="group" aria-label="Cơ hội bỏ lỡ">
              <button type="button" aria-pressed={filters.missed.length > 0} onClick={() => toggle('missed', '1')} title="Khách hỏi hàng cũ mà sale không giới thiệu">
                Cơ hội bỏ lỡ
              </button>
            </div>
              {selectOf('subcats', ctx.subcats, 'Mọi nhóm hàng chi tiết')}
              {selectOf('offers', ctx.offers, 'Mọi cách giới thiệu')}
              {selectOf('outcomes', ctx.outcomes, 'Mọi kết quả')}
              {selectOf('stock', ctx.stock, 'Mọi tình trạng hàng')}
            </div>
          )}
        </nav>
      </div>

      <section id="secondhand-overview" className="grid kpis ins-anchor">
        <KpiCard label="Hội thoại hỏi hàng cũ" value={num(asked.length)} sub={`${num(buyUsed)} mua đồ cũ · ${num(tradeIn)} thu cũ đổi mới`} tone="blue" />
        <KpiCard label="% sale giới thiệu" value={pctText(introRate)} sub={`${num(introduced.length)} giới thiệu / ${num(asked.length)} khách hỏi`} tone="green" />
        <KpiCard label="Sale chủ động" value={num(proactive)} sub="Tự nhắc khi khách chê giá / hết hàng mới" tone="violet" />
        <KpiCard label="Cơ hội bỏ lỡ" value={num(missed)} sub={asked.length ? `${Math.round((missed / asked.length) * 100)}% khách hỏi bị bỏ qua` : 'Chưa có khách hỏi'} tone="red" />
        <KpiCard label="Không có hàng" value={num(unavailable)} sub="Nhu cầu chưa đáp ứng · đầu vào sourcing" tone="amber" />
      </section>

      <section className="card space">
        <div className="section-title">
          <h2>Xu hướng hỏi hàng cũ &amp; tỷ lệ sale giới thiệu</h2>
          <div className="ins-seg small">
            {(['day', 'week', 'month'] as Gran[]).map(g => (
              <button key={g} type="button" aria-pressed={gran === g} onClick={() => setGranularity(g)}>{g === 'day' ? 'Ngày' : g === 'week' ? 'Tuần' : 'Tháng'}</button>
            ))}
          </div>
        </div>
        <div className="chart">
          <ChartBox
            labels={periodEntries.map(e => periodLabel(e[0]))}
            datasets={[
              { label: 'Hội thoại hỏi hàng cũ', data: periodEntries.map(e => e[1].asked), type: 'bar', backgroundColor: periodEntries.map(e => (!filters.periods.length || filters.periods.includes(e[0])) ? '#2563eb99' : '#2563eb26'), borderRadius: 4, maxBarThickness: 40 } as unknown as ChartDataset,
              { label: '% sale giới thiệu', data: periodEntries.map(e => (e[1].asked ? Math.round((e[1].intro / e[1].asked) * 1000) / 10 : null)), type: 'line', yAxisID: 'y1', borderColor: '#059669', backgroundColor: '#05966922', borderWidth: 2, tension: 0.35, pointRadius: 2, spanGaps: false, datalabels: { display: false } } as unknown as ChartDataset,
            ]}
            options={{
              scales: {
                x: { grid: { display: false } },
                y: { beginAtZero: true },
                y1: { position: 'right', beginAtZero: true, suggestedMax: 100, grid: { drawOnChartArea: false }, ticks: { callback: (v: string | number) => `${v}%` } },
              },
            }}
            onPointClick={({ index }) => { const entry = periodEntries[index]; if (entry) toggle('periods', entry[0]); }}
          />
        </div>
      </section>

      <section id="secondhand-staff" className="card space ins-anchor">
        <div className="section-title">
          <h2>Tỷ lệ giới thiệu · nhân viên</h2>
          <span>Giới thiệu ÷ khách hỏi · tỷ lệ tô màu theo ngưỡng · bấm số để lọc</span>
        </div>
        <div className="ins-band-legend">
          <span className="b-good">≥ 50%</span><span className="b-ok">30–50%</span><span className="b-warn">15–30%</span><span className="b-bad">&lt; 15%</span>
        </div>
        <div className="scroll">
          <table className="ins-crtable">
            <thead><tr><th>Sale</th><th className="right">Hỏi hàng cũ</th><th className="right">Đã giới thiệu</th><th>Tỷ lệ giới thiệu</th><th className="right">Chủ động</th><th className="right">Bỏ lỡ</th></tr></thead>
            <tbody>
              {agentRows.length === 0 ? (
                <tr><td colSpan={6} className="muted">Không có hội thoại theo bộ lọc hiện tại.</td></tr>
              ) : agentRows.map(row => {
                const on = filters.agents.includes(row.agent);
                return (
                  <tr key={row.agent} className={on ? 'is-selected' : ''}>
                    <td><button type="button" className="ins-link" aria-pressed={on} onClick={() => toggle('agents', row.agent)}>{row.agent}</button></td>
                    <td className="right tnum"><b>{num(row.asked)}</b></td>
                    <td className="right tnum">{num(row.intro)}</td>
                    <td>
                      <RateCell o={{ n: row.asked, sales: row.asked, won: row.intro, lost: row.missed }} active={on}
                        onClick={() => toggle('agents', row.agent)}
                        title={`${num(row.intro)} giới thiệu / ${num(row.asked)} khách hỏi · ${num(row.missed)} bỏ lỡ · ${num(row.total)} hội thoại`} />
                    </td>
                    <td className="right tnum">{row.proactive ? <Tag tone="violet">{num(row.proactive)}</Tag> : '—'}</td>
                    <td className="right tnum">{row.missed ? <Tag tone="red">{num(row.missed)}</Tag> : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section id="secondhand-demand" className="grid split space atop ins-anchor">
        <div className="card">
          <div className="section-title"><h2>Nhu cầu theo ngành target</h2><span>bấm để lọc · đếm khách hỏi</span></div>
          <RankList entries={targetEntries} selected={filters.targets} onToggle={value => toggle('targets', value)} labelFor={value => labelFor('targets', value)} color="var(--blue)" limit={12}
            note={value => { const n = targetNoStock.get(value) || 0; return n ? `${num(n)} không có hàng` : ''; }} />
        </div>
        <div className="card">
          <div className="section-title"><h2>Nhu cầu theo nhóm hàng chi tiết</h2><span>bấm để lọc · nguồn hàng Q4</span></div>
          <RankList entries={subEntries} selected={filters.subcats} onToggle={value => toggle('subcats', value)} labelFor={value => labelFor('subcats', value)} color="var(--teal)" limit={12}
            note={value => { const n = subNoStock.get(value) || 0; return n ? `${num(n)} không có hàng` : ''; }} />
        </div>
      </section>

      <section id="secondhand-detail" className="card space ins-anchor">
        <div className="section-title">
          <h2>Chi tiết hội thoại</h2>
          <span>{num(filtered.length)} dòng theo bộ lọc · tóm tắt và trích dẫn đã ẩn PII</span>
        </div>
        <div className="scroll">
          <table className="ins-detail">
            <thead><tr><th>Ngày</th><th>Sale</th><th>Ngành target</th><th>Nhóm chi tiết</th><th>Ý định</th><th>Giới thiệu</th><th>Tồn kho</th><th>Kết quả</th></tr></thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="muted">Không có hội thoại theo bộ lọc hiện tại.</td></tr>
              ) : filtered.slice(0, detailLimit).map(r => {
                const raw = rawConversations[r.conversation_id];
                const rawPanelId = `secondhand-raw-${r.conversation_id.replace(/[^A-Za-z0-9_-]/g, '-')}`;
                                const quotes = evidenceOf(r);
                const hasDetail = Boolean(r.customer_need || r.sale_response || r.next_step);
                return (
                  <Fragment key={r.conversation_id}>
                    <tr className="online-meta-row">
                      <td className="tnum nowrap">{r.day || '—'}</td>
                      <td><b>{r.agent_name || '—'}</b></td>
                      <td><span className="ins-stage"><i style={{ background: TARGET_COLOR[parentOf(r)] || 'var(--muted)' }} />{labelFor('targets', parentOf(r))}</span></td>
                      <td className="muted">{r.ai_subcategory ? labelFor('subcats', r.ai_subcategory) : '—'}</td>
                      <td><Tag tone={INTENT_TONE[r.secondhand_intent || 'none'] || 'gray'}>{labelFor('intents', r.secondhand_intent || 'none')}</Tag></td>
                      <td><Tag tone={OFFER_TONE[r.staff_offer_type || 'none'] || 'gray'}>{labelFor('offers', r.staff_offer_type || 'none')}</Tag></td>
                      <td><Tag tone={STOCK_TONE[r.stock_status || 'unknown'] || 'gray'}>{labelFor('stock', r.stock_status || 'unknown')}</Tag></td>
                      <td><Tag tone={OUTCOME_TONE[r.outcome || 'unknown'] || 'gray'}>{labelFor('outcomes', r.outcome || 'unknown')}</Tag></td>
                    </tr>
                    {(
                      <tr>
                        <td colSpan={8} className="secondhand-summary-cell">
                          <div className="secondhand-summary-card" id={`${rawPanelId}-card`}>
                            <b className="online-summary-headline">{r.summary || 'Tóm tắt hội thoại'}</b>
                            {hasDetail ? (
                              <div className="online-summary-fields">
                                <div><span>Khách cần</span><p>{r.customer_need || '—'}</p></div>
                                <div><span>Sale đã làm</span><p>{r.sale_response || '—'}</p></div>
                                <div>
                                  <span>Kết quả &amp; bước tiếp</span>
                                  <p>
                                    <Tag tone={OUTCOME_TONE[r.outcome || 'unknown'] || 'gray'}>{labelFor('outcomes', r.outcome || 'unknown')}</Tag>
                                    {r.next_step ? ` · ${r.next_step}` : ''}
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <span className="muted">Chưa có tóm tắt chi tiết cho hội thoại này.</span>
                            )}
                            {quotes.length > 0 && (
                              <div className="secondhand-quotes">
                                {quotes.map((quote, i) => <blockquote key={i}>{quote}</blockquote>)}
                              </div>
                            )}
                            <button type="button" className="online-raw-link" aria-expanded={Boolean(raw?.open)} aria-controls={rawPanelId}
                              onClick={() => { void toggleRawConversation(r.conversation_id); }}>
                              {raw?.open ? 'Ẩn hội thoại gốc' : 'Xem hội thoại gốc'}
                            </button>
                            {raw?.open && (
                              <div className="online-raw-panel" id={rawPanelId}>
                                {raw.loading && <span className="muted">Đang tải hội thoại gốc...</span>}
                                {raw.error && (
                                  <div className="online-raw-error">
                                    <span>{raw.error}</span>
                                    <button type="button" onClick={() => { void loadRawConversation(r.conversation_id); }}>Thử lại</button>
                                  </div>
                                )}
                                {raw.data && <RawTranscript data={raw.data} />}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length > detailLimit && (
          <button type="button" className="ins-more" onClick={() => setDetailLimit(n => n + 200)}>Xem thêm {num(Math.min(200, filtered.length - detailLimit))} dòng · còn {num(filtered.length - detailLimit)}</button>
        )}
      </section>
    </>
  );
}

function RawTranscript({ data }: { data: OnlineRawConversationResponse }) {
  return (
    <div className="online-raw-transcript">
      <div className="online-raw-summary">
        <span>{num(data.returned_messages)} / {num(data.total_messages)} tin nhắn</span>
        {data.truncated && <b>Chỉ hiển thị 500 tin đầu tiên</b>}
      </div>
      {data.messages.length === 0 && <span className="muted">Hội thoại chưa có tin nhắn raw.</span>}
      {data.messages.map(message => <RawMessageView key={message.message_id} message={message} />)}
    </div>
  );
}

function RawMessageView({ message }: { message: OnlineRawMessage }) {
  const roleLabel = message.role === 'agent'
    ? 'NV'
    : message.role === 'visitor' ? 'Khách' : message.role === 'system' ? 'Hệ thống' : 'Không rõ';
  return (
    <div className={`online-raw-message ${message.role}`}>
      <div className="online-raw-meta">
        <b>{roleLabel}{message.speaker ? ` · ${message.speaker}` : ''}</b>
        <span>{message.event_time || 'Không có thời gian'} · type {message.message_type}</span>
      </div>
      <div className="online-raw-text">{message.text || '—'}</div>
    </div>
  );
}
